import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { App, Button, Input, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { listWorkItems, assignWorkItem, dispositionWorkItem } from '@/api/workItems';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { WorkItem, WorkItemQueue, WorkItemStatus, WorkItemType } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';
import { useAuth } from '@/auth/OlAuthContext';
import { DATA_TOUR } from '@/edu/tourModel';
import {
  AgeBadge,
  MemoryBadge,
  PriorityBadge,
  SlaBadge,
  WorkItemStatusTag,
  WorkItemTypeBadge,
} from '@/workbench/badges';
import { WORK_ITEM_TYPE_LABEL } from '@/workbench/meta';
import { isTypingTarget, queueAction } from '@/workbench/keymap';
import { ShortcutsReference } from '@/workbench/ShortcutsReference';
import { WorkItemDrawer } from '@/workbench/WorkItemDrawer';

const { Text } = Typography;
const { CheckableTag } = Tag;

const QUEUE_TABS: { key: WorkItemQueue; label: string }[] = [
  { key: 'mine', label: 'My work' },
  { key: 'team', label: 'Team' },
  { key: 'unassigned', label: 'Unassigned' },
];

const TYPE_OPTIONS: WorkItemType[] = ['HUMAN_TASK', 'APPROVAL', 'SIGNAL_REVIEW', 'RFI_FOLLOWUP'];
const STATUS_OPTIONS: WorkItemStatus[] = ['OPEN', 'IN_PROGRESS', 'WAITING', 'DONE', 'CANCELLED'];

/**
 * The always-on hint bar. It carries only the four chords that drive the SCAN loop; everything
 * else (the disposition numbers, the drawer tabs) is printed on the control it fires, and the
 * complete map lives one click away in Shortcuts. Hour-11 legibility beats completeness here.
 */
function KbdHints() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div className="ol-kbd-hints" aria-hidden>
        <span>
          <span className="ol-kbd">j</span>
          <span className="ol-kbd">k</span>
          move
        </span>
        <span>
          <span className="ol-kbd">↵</span>
          open
        </span>
        <span>
          <span className="ol-kbd">a</span>
          assign to me
        </span>
        <span>
          <span className="ol-kbd">1</span>
          <span className="ol-kbd">…</span>
          dispose
        </span>
      </div>
      <ShortcutsReference />
    </div>
  );
}

export function WorkbenchPage() {
  usePageTitle('Workbench');
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [queue, setQueue] = useState<WorkItemQueue>('mine');
  const [typeFilter, setTypeFilter] = useState<WorkItemType | undefined>();
  const [statusFilter, setStatusFilter] = useState<WorkItemStatus | undefined>();
  const entityFilter = searchParams.get('entityId') ?? undefined;

  const [items, setItems] = useState<WorkItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const [activeIndex, setActiveIndex] = useState(-1);
  const [openItemId, setOpenItemId] = useState<string | null>(searchParams.get('item'));

  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [bulkNote, setBulkNote] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const gridRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    setError(null);
    setNow(Date.now());
    try {
      const res = await listWorkItems({
        queue,
        type: typeFilter,
        status: statusFilter,
        entityId: entityFilter,
        page,
        pageSize,
      });
      setItems(res.items);
      setTotal(res.totalCount);
      setActiveIndex(res.items.length ? 0 : -1);
      setSelectedKeys([]);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else setError(n.message);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [queue, typeFilter, statusFilter, entityFilter, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const assignSelf = useCallback(
    async (item: WorkItem) => {
      try {
        await assignWorkItem(item.itemId, undefined);
        message.success(`Assigned “${item.title}” to you`);
        void load();
      } catch (e) {
        const n = normalizeApiError(e);
        if (n.code === 'ALREADY_DONE') {
          message.warning('That item was already actioned. Refreshing.');
          void load();
        } else if (isNotAvailable(n)) {
          message.info('The assign endpoint is not live yet (Track A pending).');
        } else {
          message.error(n.message);
        }
      }
    },
    [load, message],
  );

  // Keyboard-first navigation (j/k/Enter/a), resolved through the shared keymap table so the
  // Shortcuts reference and the behaviour can never disagree. Window-level so it works without a
  // click first; it stands down while typing and while the drawer owns the keyboard.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (openItemId) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (items.length === 0) return;
      const action = queueAction(e.key);
      if (!action) return;
      if (action === 'next') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i < 0 ? 0 : i + 1, items.length - 1));
      } else if (action === 'prev') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i < 0 ? 0 : i - 1, 0));
      } else if (activeIndex >= 0 && activeIndex < items.length) {
        e.preventDefault();
        if (action === 'open') setOpenItemId(items[activeIndex].itemId);
        else void assignSelf(items[activeIndex]);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, activeIndex, openItemId, assignSelf]);

  // Scroll the active row into view.
  useEffect(() => {
    if (activeIndex < 0) return;
    const rows = gridRef.current?.querySelectorAll('tbody .ant-table-row');
    const el = rows?.[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const bulkClear = useCallback(async () => {
    if (selectedKeys.length === 0) return;
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    for (const key of selectedKeys) {
      try {
        await dispositionWorkItem(String(key), 'CLEAR', { note: bulkNote.trim() || undefined });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setBulkBusy(false);
    setBulkNote('');
    if (ok) message.success(`Cleared ${ok} signal${ok === 1 ? '' : 's'}`);
    if (fail) message.warning(`${fail} could not be cleared (not live, or already actioned).`);
    void load();
  }, [selectedKeys, bulkNote, load, message]);

  const openEntity = useCallback(
    (e: React.MouseEvent, entityId: string) => {
      e.stopPropagation();
      navigate(`/entities/${encodeURIComponent(entityId)}`);
    },
    [navigate],
  );

  const mineOf = useCallback(
    (assignee?: string) =>
      assignee != null && [user?.sub, user?.email, user?.name].filter(Boolean).includes(assignee),
    [user],
  );

  const columns: ColumnsType<WorkItem> = useMemo(
    () => [
      {
        title: 'Priority',
        dataIndex: 'priority',
        key: 'priority',
        width: 96,
        render: (_, r) => <PriorityBadge priority={r.priority} />,
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 150,
        render: (_, r) => <WorkItemTypeBadge type={r.type} />,
      },
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        render: (_, r) => (
          <div style={{ minWidth: 220 }}>
            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {r.title}
              {r.type === 'SIGNAL_REVIEW' && r.priorDisposition && <MemoryBadge prior={r.priorDisposition} />}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {r.entityId && (
                <a
                  className="ol-mono"
                  style={{ fontSize: 10.5 }}
                  onClick={(e) => openEntity(e, r.entityId!)}
                  role="link"
                  tabIndex={-1}
                >
                  {r.entityId}
                </a>
              )}
              <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
                {r.refId}
              </Text>
            </div>
          </div>
        ),
      },
      {
        title: 'SLA',
        dataIndex: 'slaDueAt',
        key: 'sla',
        width: 132,
        render: (_, r) => <SlaBadge slaDueAt={r.slaDueAt} createdAt={r.createdAt} now={now} />,
      },
      {
        title: 'Assignee',
        dataIndex: 'assignee',
        key: 'assignee',
        width: 140,
        render: (_, r) =>
          r.assignee ? (
            <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {r.assignee}
              {mineOf(r.assignee) && (
                <Tag bordered={false} color="processing" style={{ fontSize: 9.5, margin: 0 }}>
                  you
                </Tag>
              )}
            </span>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              unassigned
            </Text>
          ),
      },
      {
        title: 'Age',
        dataIndex: 'createdAt',
        key: 'age',
        width: 92,
        render: (_, r) => <AgeBadge createdAt={r.createdAt} now={now} />,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 128,
        render: (_, r) => <WorkItemStatusTag status={r.status} />,
      },
    ],
    [now, openEntity, mineOf],
  );

  const rowSelection: TableProps<WorkItem>['rowSelection'] =
    typeFilter === 'SIGNAL_REVIEW'
      ? {
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          getCheckboxProps: (r) => ({ disabled: r.status === 'DONE' || r.status === 'CANCELLED' }),
        }
      : undefined;

  const body = unavailable ? (
    <EmptyState
      icon={<InboxOutlined />}
      title="Investigator workbench"
      wave="PS-3"
      description="Your queues, SLAs and dispositions land here once the work-items API is live (Track A, PS-3). This is the daily surface for the scarce analyst — everything one keyboard-driven list."
      bullets={[
        'My work / Team / Unassigned queues with SLA countdowns and overdue flags',
        'Human tasks, approvals, signal reviews and RFI follow-ups in one feed',
        'Per-type disposition: decision options, CLEAR/ESCALATE/LINK, RFI outcomes',
        'Prior-disposition memory so a cleared signal stays cleared',
      ]}
    />
  ) : (
    <div
      ref={gridRef}
      role="region"
      aria-label="Work queue — use j and k to move, Enter to open, a to assign to yourself"
      aria-keyshortcuts="j k a Enter"
      data-tour={DATA_TOUR.workbenchList}
    >
      <Table<WorkItem>
        rowKey="itemId"
        loading={loading && items.length > 0}
        locale={{
          emptyText: loading ? (
            <TableSkeleton label="Loading your queue" />
          ) : error ? (
            <ErrorState what="your work queue" detail={error} onRetry={() => void load()} compact />
          ) : (
            <EmptyState
              compact
              icon={<InboxOutlined />}
              title={queue === 'mine' ? 'Nothing assigned to you' : 'This queue is clear'}
              description={
                queue === 'mine'
                  ? 'No item under these filters is yours. Start a shift on the Unassigned queue — pick an item, press a to take it, and it moves here.'
                  : 'Nothing matches these filters. Work items arrive when a run reaches a human step, a signal needs triage, a change needs a second reviewer, or an RFI goes unanswered.'
              }
            />
          ),
        }}
        columns={columns}
        dataSource={items}
        size="small"
        rowSelection={rowSelection}
        rowClassName={(_, index) => (index === activeIndex ? 'ol-wb-row--active' : '')}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100],
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        onRow={(r, index) => ({
          onClick: () => {
            setActiveIndex(index ?? -1);
            setOpenItemId(r.itemId);
          },
          onKeyDown: (e) => {
            if (e.key === 'Enter') setOpenItemId(r.itemId);
          },
          tabIndex: 0,
          'aria-label': `${WORK_ITEM_TYPE_LABEL[r.type]}: ${r.title}`,
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );

  return (
    <div className="ol-page">
      <PageHeader
        title="Workbench"
        icon={<InboxOutlined />}
        subtitle="Your case work — human tasks, approvals, signal reviews and RFI follow-ups — in one keyboard-driven queue. Scan the list, open a row, dispose it."
        extra={<KbdHints />}
      />

      {entityFilter && (
        <div style={{ marginBottom: 10 }}>
          <Tag
            closable
            bordered={false}
            color="processing"
            onClose={() => {
              searchParams.delete('entityId');
              setSearchParams(searchParams, { replace: true });
            }}
          >
            entity: {entityFilter}
          </Tag>
        </div>
      )}

      <div data-tour={DATA_TOUR.workbenchQueues}>
        <Tabs
          activeKey={queue}
          onChange={(k) => {
            setQueue(k as WorkItemQueue);
            setPage(1);
          }}
          items={QUEUE_TABS.map((q) => ({ key: q.key, label: q.label }))}
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>
            Type
          </Text>
          <CheckableTag
            checked={typeFilter === undefined}
            onChange={() => {
              setTypeFilter(undefined);
              setSelectedKeys([]);
              setPage(1);
            }}
          >
            All
          </CheckableTag>
          {TYPE_OPTIONS.map((t) => (
            <CheckableTag
              key={t}
              checked={typeFilter === t}
              onChange={(c) => {
                setTypeFilter(c ? t : undefined);
                setSelectedKeys([]);
                setPage(1);
              }}
            >
              {WORK_ITEM_TYPE_LABEL[t]}
            </CheckableTag>
          ))}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>
            Status
          </Text>
          <CheckableTag
            checked={statusFilter === undefined}
            onChange={() => {
              setStatusFilter(undefined);
              setPage(1);
            }}
          >
            All
          </CheckableTag>
          {STATUS_OPTIONS.map((s) => (
            <CheckableTag
              key={s}
              checked={statusFilter === s}
              onChange={(c) => {
                setStatusFilter(c ? s : undefined);
                setPage(1);
              }}
            >
              {s}
            </CheckableTag>
          ))}
        </div>
      </div>

      {/* Bulk triage (SIGNAL_REVIEW only) — shared-note CLEAR. */}
      {typeFilter === 'SIGNAL_REVIEW' && selectedKeys.length > 0 && (
        <div
          className="ol-card"
          style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}
        >
          <Text strong style={{ fontSize: 12 }}>
            {selectedKeys.length} selected
          </Text>
          <Input
            placeholder="Shared clear note (optional)"
            value={bulkNote}
            onChange={(e) => setBulkNote(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <Button type="primary" loading={bulkBusy} onClick={bulkClear}>
            Clear selected
          </Button>
          <Button onClick={() => setSelectedKeys([])}>Cancel</Button>
        </div>
      )}

      {body}

      <WorkItemDrawer
        itemId={openItemId}
        onClose={() => {
          setOpenItemId(null);
          if (searchParams.get('item')) {
            searchParams.delete('item');
            setSearchParams(searchParams, { replace: true });
          }
        }}
        onChanged={() => void load()}
      />
    </div>
  );
}
